import { useEffect, useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { updateUserProfile } from '@/features/users';

import { startPhoneVerification, confirmPhoneVerification } from '@/features/users/usersThunks';
import { selectPhoneVerificationError, selectPhoneVerificationInProgress } from '@/features/users/usersSlice';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { formatNanpPhone, isValidNanpDigits, nanpDigits, toE164FromNanp } from '@/lib/validation/phone';
import { selectCurrentApplicationUserId, selectCurrentUserProfile } from '@/store/selectors/authorizationSelectors';

type VerifyPhoneStepProps = {
  onSuccess?: () => void;
  onSkip?: () => void;
};

const RESEND_SECONDS = 60;

export default function VerifyPhoneStep({ onSuccess, onSkip }: VerifyPhoneStepProps) {
  const dispatch = useAppDispatch();

  const applicationUserId = useAppSelector(selectCurrentApplicationUserId);
  const profile = useAppSelector(selectCurrentUserProfile);

  // ✅ global flags from slice
  const isVerifyingPhone = useAppSelector(selectPhoneVerificationInProgress);
  const phoneVerificationError = useAppSelector(selectPhoneVerificationError);

  const [otpValue, setOtpValue] = useState('');
  const [hasSentCode, setHasSentCode] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // local UI flags for editing phone + saving phone (different from verification calls)
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editedPhone, setEditedPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const phoneE164 = profile?.phone || '';

  const phoneDisplay = useMemo(() => {
    if (!phoneE164) return 'your phone number';
    // phoneE164 is +1##########
    const formatted = formatNanpPhone(phoneE164);
    return formatted ? `+1 ${formatted}` : phoneE164;
  }, [phoneE164]);

  useEffect(() => {
    if (!isEditingPhone) {
      setEditedPhone(phoneE164 ? formatNanpPhone(phoneE164) : '');
      setPhoneError(null);
    }
  }, [phoneE164, isEditingPhone]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  // Optional: show errors from slice as toast
  useEffect(() => {
    if (phoneVerificationError) toast.error(phoneVerificationError);
  }, [phoneVerificationError]);

  const canSend = !!applicationUserId && !!phoneE164 && !isEditingPhone && !isSavingPhone && resendCountdown <= 0;
  const canVerify =
    !!applicationUserId && !!phoneE164 && hasSentCode && otpValue.length === 6 && !isEditingPhone && !isSavingPhone;

  const startPhoneEdit = () => {
    setIsEditingPhone(true);
    setEditedPhone(phoneE164 ? formatNanpPhone(phoneE164) : '');
    setPhoneError(null);
  };

  const cancelPhoneEdit = () => {
    setIsEditingPhone(false);
    setEditedPhone(phoneE164 ? formatNanpPhone(phoneE164) : '');
    setPhoneError(null);
  };

  const savePhoneEdit = async () => {
    if (!profile || !applicationUserId) {
      toast.error('Unable to update phone right now.');
      return;
    }

    const digits = nanpDigits(editedPhone);
    if (!isValidNanpDigits(digits)) {
      setPhoneError('Enter a valid North American phone number.');
      return;
    }

    const nextProfile = {
      ...profile,
      phone: toE164FromNanp(editedPhone), // ✅ +1##########
      phoneVerified: false, // ✅ changing phone invalidates verification
    };

    try {
      setIsSavingPhone(true);

      // Persist updated phone to API (and slice updates profile)
      await dispatch(updateUserProfile({ profile: nextProfile })).unwrap();

      setIsEditingPhone(false);

      // Reset verification UI
      setHasSentCode(false);
      setOtpValue('');
      setResendCountdown(0);

      toast.success('Phone number updated.');
    } catch (err) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Failed to update phone number.');
    } finally {
      setIsSavingPhone(false);
    }
  };

  const handleSendCode = async () => {
    if (!applicationUserId) return;

    if (!phoneE164) {
      toast.error('Add a phone number in your profile first.');
      return;
    }

    if (resendCountdown > 0) return;

    try {
      // ✅ call API thunk
      await dispatch(startPhoneVerification({ phoneE164 })).unwrap();

      setHasSentCode(true);
      setResendCountdown(RESEND_SECONDS);
      setOtpValue('');

      toast.success(`Code sent to ${phoneDisplay}.`);
    } catch (err) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Failed to send verification code.');
    }
  };

  const handleVerifyCode = async () => {
    if (!applicationUserId) return;
    if (otpValue.length !== 6) return;

    if (!phoneE164) {
      toast.error('Missing phone number.');
      return;
    }

    try {
      // ✅ call API thunk
      await dispatch(confirmPhoneVerification({ phoneE164, code: otpValue })).unwrap();

      toast.success('Phone verified!');
      onSuccess?.();
    } catch (err) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Failed to verify code.');
    }
  };

  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle>Verify Your Phone</CardTitle>
        <CardDescription>We will send a 6-digit SMS code to your number.</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="bg-muted/40 flex items-start justify-between rounded-md border p-3">
          <div className="space-y-1">
            <p className="text-muted-foreground text-xs">SMS destination</p>

            {!isEditingPhone ? (
              <p className="text-sm font-medium">{phoneDisplay}</p>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 text-sm">+1</span>
                  <Input
                    value={editedPhone}
                    onChange={(e) => {
                      setEditedPhone(formatNanpPhone(e.target.value));
                      if (phoneError) setPhoneError(null);
                    }}
                    placeholder="(000) 000-0000"
                    className="w-48 pl-10"
                    inputMode="tel"
                    autoFocus
                  />
                </div>
                {phoneError && <p className="text-destructive text-xs">{phoneError}</p>}
              </div>
            )}
          </div>

          {!isEditingPhone ? (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={startPhoneEdit}
              aria-label="Edit phone number"
              disabled={isVerifyingPhone}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={cancelPhoneEdit} disabled={isSavingPhone}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={savePhoneEdit} disabled={isSavingPhone}>
                {isSavingPhone ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={otpValue} onChange={setOtpValue} disabled={!hasSentCode || isVerifyingPhone}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <p className="text-muted-foreground text-center text-sm">
            {resendCountdown > 0
              ? `You can resend a code in ${resendCountdown}s`
              : hasSentCode
                ? 'You can request a new code now.'
                : 'Send a code to continue.'}
          </p>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleSendCode}
              className="border-border/70 hover:border-border flex-1"
              disabled={!canSend || isVerifyingPhone}
            >
              {isVerifyingPhone ? 'Sending...' : hasSentCode ? 'Resend code' : 'Send code'}
            </Button>

            <Button
              type="button"
              onClick={handleVerifyCode}
              className="flex-1"
              disabled={!canVerify || isVerifyingPhone}
            >
              {isVerifyingPhone ? 'Verifying...' : 'Verify code'}
            </Button>
          </div>

          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => onSkip?.()}>
              Skip (Dev Only)
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
